import type { GrowthBook } from '@growthbook/growthbook'
import {
  DEFAULT_SIGN_UP_METHODS_CONFIG,
  DEFAULT_WEB_FUNNELS_CONFIG_FILE,
  GROWTHBOOK_FUNNELS_CONFIG_KEY,
  type ISignUpMethodsConfigProps,
  type IWebFunnelsConfigFile,
} from 'root-constants'
import { useZustandStore } from 'zustandGlobalStore'

type TRawWebFunnelsConfig = Record<string, unknown>

const isRecord = (value: unknown): value is TRawWebFunnelsConfig =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const getBooleanValue = (value: unknown, fallback: boolean): boolean => {
  if (typeof value === 'boolean') return value

  return fallback
}

const getNullableBooleanValue = (value: unknown): boolean | null =>
  typeof value === 'boolean' ? value : null

const getSignUpMethodsConfig = (
  rawData: unknown,
): ISignUpMethodsConfigProps => {
  if (!isRecord(rawData)) return DEFAULT_SIGN_UP_METHODS_CONFIG

  const otherMethodsShown = isRecord(rawData.other_methods_shown)
    ? rawData.other_methods_shown
    : {}

  return {
    chooseOtherMethodsShown: getBooleanValue(
      rawData.choose_other_methods_shown,
      DEFAULT_SIGN_UP_METHODS_CONFIG.chooseOtherMethodsShown,
    ),
    otherMethodsShown: {
      facebook: getBooleanValue(
        otherMethodsShown.facebook,
        DEFAULT_SIGN_UP_METHODS_CONFIG.otherMethodsShown.facebook,
      ),
      apple: getBooleanValue(
        otherMethodsShown.apple,
        DEFAULT_SIGN_UP_METHODS_CONFIG.otherMethodsShown.apple,
      ),
      google: getBooleanValue(
        otherMethodsShown.google,
        DEFAULT_SIGN_UP_METHODS_CONFIG.otherMethodsShown.google,
      ),
    },
  }
}

const getWebFunnelsConfigFromRawData = (
  rawData?: string | TRawWebFunnelsConfig | null,
): IWebFunnelsConfigFile => {
  if (!rawData) return DEFAULT_WEB_FUNNELS_CONFIG_FILE

  let parsedData: unknown = rawData

  if (typeof rawData === 'string') {
    try {
      parsedData = JSON.parse(rawData)
    } catch {
      return DEFAULT_WEB_FUNNELS_CONFIG_FILE
    }
  }

  if (!isRecord(parsedData)) return DEFAULT_WEB_FUNNELS_CONFIG_FILE

  return {
    paypalMethodShown: getBooleanValue(
      parsedData.paypal_method_shown,
      DEFAULT_WEB_FUNNELS_CONFIG_FILE.paypalMethodShown,
    ),
    cardMethodShown: getBooleanValue(
      parsedData.card_method_shown,
      DEFAULT_WEB_FUNNELS_CONFIG_FILE.cardMethodShown,
    ),
    applePayMethodShown: getBooleanValue(
      parsedData.apple_pay_method_shown,
      DEFAULT_WEB_FUNNELS_CONFIG_FILE.applePayMethodShown,
    ),
    googlePayMethodShown: getBooleanValue(
      parsedData.google_pay_method_shown,
      DEFAULT_WEB_FUNNELS_CONFIG_FILE.googlePayMethodShown,
    ),
    signUpMethods: getSignUpMethodsConfig(parsedData.sign_up_methods),
    caCmpl: getNullableBooleanValue(parsedData.ca_cmpl),
    usCmpl: getNullableBooleanValue(parsedData.us_cmpl),
    nycCmpl: getNullableBooleanValue(parsedData.nyc_cmpl),
    gbCmpl: getNullableBooleanValue(parsedData.gb_cmpl),
  }
}

export const getWebFunnelsConfig = (
  growthbook: GrowthBook | null,
): IWebFunnelsConfigFile => {
  if (!growthbook) return DEFAULT_WEB_FUNNELS_CONFIG_FILE

  const { value } = growthbook.evalFeature(GROWTHBOOK_FUNNELS_CONFIG_KEY)

  return getWebFunnelsConfigFromRawData(
    value as string | TRawWebFunnelsConfig | null,
  )
}

export const getFunnelsConfig = (): IWebFunnelsConfigFile =>
  getWebFunnelsConfig(useZustandStore.getState().growthbook)
